import { useSearchParams } from 'react-router-dom';

/**
 * URL 쿼리스트링 획득 hook 함수
 * @returns
 */
const useQuery = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = Object.fromEntries(searchParams.entries());

    /**
     * @param search : location.search(menuId, webviewType 유지) or menu-config.[env].xml에 등록되어 있는 url
     * @param query : navigation options query
     * @returns query string
     */
    const mergeQuery = (search: string | URLSearchParams, query?: string | URLSearchParams) => {
        const current = new URLSearchParams(search);
        const next = new URLSearchParams(
            query instanceof URLSearchParams ? query?.toString() : (query ?? ''),
        );

        next.forEach((value, key) => {
            current.set(key, value);
        });

        return `${current.toString()}`;
    };

    /**
     * T 타입의 데이터를 URLSearchParams 객체로 변환한다.
     * @param data T 타입의 데이터
     * @returns T 타입의 데이터를 URLSearchParams 변환한 결과
     */
    const toURLSearchParams = <T extends Object = {}>(data: T): URLSearchParams => {
        const searchParams = new URLSearchParams();

        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            searchParams.set(key, value);
        });
        return searchParams;
    };

    /**
     * T 타입의 데이터를 쿼리스트링 문자열 형태로 변환한다.
     * @param data T 타입의 데이터 객체
     */
    const toQueryString = <T extends object = {}>(data: T): string => {
        const searchParams = toURLSearchParams(data);
        return searchParams.toString();
    };

    /**
     * URL에 쿼리 정보를 추가한다.
     * @param url URL 문자열
     * @param key 쿼리 키
     * @param value 쿼리 값
     * @returns
     */
    const appendQuery = (url: string, key: string, value: string) => {
        if (!url) {
            return '';
        }

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${key}=${encodeURIComponent(value)}`;
    };

    /**
     * URL에 쿼리스트링을 append 시킨다.
     * @param url URL 문자열
     * @param queryString 쿼리스트링 문자열 또는 URLSearchParams 객체
     */
    const appendQueryString = (url: string, queryString: string | URLSearchParams) => {
        if (!url) {
            return '';
        }

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${queryString.toString()}`;
    };

    const getQuery = (key: string): string | null => {
        const params = new URLSearchParams(window.location.search);
        return params.get(key);
    };

    const setParam = (key: string, value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(key, value);
            return next;
        });
    };

    const queryToObject = (queryString: string): Record<string, string> => {
        const query = queryString.startsWith('?') ? queryString.slice(1) : queryString;
        return Object.fromEntries(new URLSearchParams(query));
    };

    return {
        query,
        mergeQuery,
        toQueryString,
        toURLSearchParams,
        appendQuery,
        appendQueryString,
        setParam,
        getQuery,
        queryToObject,
    };
};

export { useQuery };
